import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/pause/motion";

const faqs = [
  {
    q: "I've never done yoga before — is that okay?",
    a: "Completely. Viniyoga is built around meeting you exactly where you are. We start slow, adapt every pose to your body, and never chase a shape for its own sake.",
  },
  {
    q: "Do you teach online or in person?",
    a: "Both. 1-on-1 sessions and weekly classes are available live online or in person, and retreats are always in person, out in nature.",
  },
  {
    q: "What actually is Viniyoga?",
    a: "It's the tradition of adapting the practice to the individual rather than the individual to the practice. Function over form — which is why it stays kind to your body for life.",
  },
  {
    q: "What are the retreats like?",
    a: "Small groups, off-grid settings, and an unhurried rhythm — breathwork, gentle movement, sattvic food, walks, and plenty of stillness. You leave lighter than you arrived.",
  },
  {
    q: "Is there anything I need to bring or prepare?",
    a: "Just comfortable clothing and an open mind. Mats and props are provided for in-person sessions; for online, a quiet corner is all you need.",
  },
  {
    q: "How do I join or book a spot?",
    a: "Join the list above and you'll be first to hear about new class times and retreat dates. We keep cohorts intentionally small.",
  },
];

const PlusIcon = ({ open }) => (
  <motion.span
    animate={{ rotate: open ? 45 : 0 }}
    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center"
  >
    <span className="absolute h-[1.5px] w-5 bg-current" />
    <span className="absolute h-5 w-[1.5px] bg-current" />
  </motion.span>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="bg-[#FAF7F2] py-24 md:py-32">
      <div className="mx-auto max-w-4xl px-5 md:px-10">
        <Reveal>
          <p className="font-body text-xs uppercase tracking-[0.28em] text-[#8C8070] mb-4">Questions</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#1A1A1A] mb-14">
            Good to <span className="italic text-[#3B5A3A]">know</span>.
          </h2>
        </Reveal>

        <div className="border-t border-[#1A1A1A]/12">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b border-[#1A1A1A]/12">
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span
                    className={`font-display text-2xl md:text-3xl transition-colors duration-300 ${
                      isOpen ? "text-[#3B5A3A]" : "text-[#1A1A1A] group-hover:text-[#3B5A3A]"
                    }`}
                  >
                    {f.q}
                  </span>
                  <span className="text-[#1A1A1A]">
                    <PlusIcon open={isOpen} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="pb-7 pr-10 font-body text-base md:text-lg leading-relaxed text-[#1A1A1A]/65 max-w-2xl"
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
